// IMPORTANT!!!!! This module was copied from the Aria repository (https://msasg.visualstudio.com/Shared%20Data/_git/Aria.SDK.WebJS?) because
// we want the browsers we use for things like OCV feedback to align with our telemetry. Our long term strategy should be to work with the OCV
// team to share this module. VSO: 3722991 tracks removing the duplication.

/**
 * @author Abhilash Panwar (abpanwar)
 * @copyright Microsoft 2018
 */
export const BROWSERS = {
  MSIE: 'MSIE',
  CHROME: 'Chrome',
  FIREFOX: 'Firefox',
  SAFARI: 'Safari',
  EDGE: 'Edge',
  EDGE_CHROMIUM: 'Edge Chromium',
  ELECTRON: 'Electron',
  SKYPE_SHELL: 'SkypeShell',
  PHANTOMJS: 'PhantomJS',
  OPERA: 'Opera'
};

export const OPERATING_SYSTEMS = {
  WINDOWS: 'Windows',
  MACOSX: 'Mac OS X',
  WINDOWS_PHONE: 'Windows Phone',
  WINDOWS_RT: 'Windows RT',
  IOS: 'iOS',
  ANDROID: 'Android',
  LINUX: 'Linux',
  UNKNOWN: 'Unknown'
};

const OSNAMEREGEX = {
  WIN: /(windows|win32)/i,
  WINRT: / arm;/i,
  WINPHONE: /windows\sphone\s\d+\.\d+/i,
  OSX: /(macintosh|mac os x)/i,
  IOS: /(iPad|iPhone|iPod)(?=.*like Mac OS X)/i,
  LINUX: /(linux|joli|[kxln]?ubuntu|debian|[open]*suse|gentoo|arch|slackware|fedora|mandriva|centos|pclinuxos|redhat|zenwalk)/i,
  ANDROID: /android/i
};

const VERSION_MAPPINGS: { [key: string]: string } = {
  '5.1': 'XP',
  '6.0': 'Vista',
  '6.1': '7',
  '6.2': '8',
  '6.3': '8.1',
  '10.0': '10'
};

const REGEX_VERSION = '([\\d,.]+)';
const REGEX_VERSION_MAC = '([\\d,_,.]+)';
const UNKNOWN = 'Unknown';
const UNDEFINED = 'undefined';

function userAgentContainsString(searchString: string, userAgent: string) {
  return userAgent.indexOf(searchString) > -1;
}

function getUserAgent(): string {
  if (typeof navigator !== UNDEFINED) {
    return navigator.userAgent;
  }

  return '';
}

function getBrowserName(userAgent: string) {
  // Check for Opera first
  if (userAgentContainsString('OPR/', userAgent)) {
    return BROWSERS.OPERA;
  }
  // Check for Phantom JS
  if (userAgentContainsString(BROWSERS.PHANTOMJS, userAgent)) {
    return BROWSERS.PHANTOMJS;
  }
  // Check for Edge
  if (userAgentContainsString(BROWSERS.EDGE, userAgent)) {
    return BROWSERS.EDGE;
  }

  // Check for Edge Chromium
  if (userAgentContainsString('Edg', userAgent)) {
    return BROWSERS.EDGE_CHROMIUM;
  }
  // Check for Electron
  if (userAgentContainsString(BROWSERS.ELECTRON, userAgent)) {
    return BROWSERS.ELECTRON;
  }
  // Check for Chrome
  if (userAgentContainsString(BROWSERS.CHROME, userAgent)) {
    return BROWSERS.CHROME;
  }
  // Check for Internet Explorer
  if (userAgentContainsString('Trident', userAgent)) {
    return BROWSERS.MSIE;
  }
  // Check for Firefox
  if (userAgentContainsString(BROWSERS.FIREFOX, userAgent)) {
    return BROWSERS.FIREFOX;
  }
  // Check for Safari
  if (userAgentContainsString(BROWSERS.SAFARI, userAgent)) {
    return BROWSERS.SAFARI;
  }
  // Check for Skype shell
  if (userAgentContainsString(BROWSERS.SKYPE_SHELL, userAgent)) {
    return BROWSERS.SKYPE_SHELL;
  }
  return undefined;
}

function getBrowserVersion(userAgent: string, browserName: string) {
  if (browserName === BROWSERS.MSIE) {
    return getIeVersion(userAgent);
  }

  // Edge Chromium displays it's name as Edg instead of Edge, so we need to special case it in the version search
  const browserNameToUse = browserName === BROWSERS.EDGE_CHROMIUM ? 'Edg' : browserName;
  return getOtherVersion(browserNameToUse, userAgent);
}

function getIeVersion(userAgent: string) {
  let classicIeVersionMatches = userAgent.match(new RegExp(BROWSERS.MSIE + ' ' + REGEX_VERSION));
  if (classicIeVersionMatches) {
    return classicIeVersionMatches[1];
  }
  {
    let ieVersionMatches = userAgent.match(new RegExp('rv:' + REGEX_VERSION));
    if (ieVersionMatches) {
      return ieVersionMatches[1];
    }
  }
  return '';
}

function getOtherVersion(browserString: string, userAgent: string) {
  if (browserString === BROWSERS.SAFARI) {
    // tslint:disable-next-line: no-parameter-reassignment
    browserString = 'Version';
  }
  let matches = userAgent.match(new RegExp(browserString + '/' + REGEX_VERSION));
  if (matches) {
    return matches[1];
  }
  return undefined;
}

const getDelimiter = (versionString: string) => {
  if (versionString.indexOf('.') > -1) {
    return '.';
  }
  if (versionString.indexOf('_') > -1) {
    return '_';
  }
  return null;
};

const getOsName = (userAgent: string) => {
  if (userAgent.match(OSNAMEREGEX.WINPHONE)) {
    return OPERATING_SYSTEMS.WINDOWS_PHONE;
  }
  if (userAgent.match(OSNAMEREGEX.WINRT)) {
    return OPERATING_SYSTEMS.WINDOWS_RT;
  }
  if (userAgent.match(OSNAMEREGEX.IOS)) {
    return OPERATING_SYSTEMS.IOS;
  }
  if (userAgent.match(OSNAMEREGEX.ANDROID)) {
    return OPERATING_SYSTEMS.ANDROID;
  }
  if (userAgent.match(OSNAMEREGEX.LINUX)) {
    return OPERATING_SYSTEMS.LINUX;
  }
  if (userAgent.match(OSNAMEREGEX.OSX)) {
    return OPERATING_SYSTEMS.MACOSX;
  }
  if (userAgent.match(OSNAMEREGEX.WIN)) {
    return OPERATING_SYSTEMS.WINDOWS;
  }
  return UNKNOWN;
};

const getOsVersion = (userAgent: string, osName: string) => {
  if (osName === OPERATING_SYSTEMS.WINDOWS) {
    return getGenericOsVersion(userAgent, 'Windows NT');
  }
  if (osName === OPERATING_SYSTEMS.ANDROID) {
    return getGenericOsVersion(userAgent, osName);
  }
  if (osName === OPERATING_SYSTEMS.MACOSX) {
    return getMacOsxVersion(userAgent);
  }
  return UNKNOWN;
};

const getGenericOsVersion = (userAgent: string, osName: string) => {
  const ntVersionMatches = userAgent.match(new RegExp(osName + ' ' + REGEX_VERSION));
  if (ntVersionMatches) {
    if (VERSION_MAPPINGS[ntVersionMatches[1]]) {
      return VERSION_MAPPINGS[ntVersionMatches[1]];
    }
    return ntVersionMatches[1];
  }
  return UNKNOWN;
};

const getMacOsxVersion = (userAgent: string) => {
  const macOsxVersionInUserAgentMatches = userAgent.match(
    new RegExp(OPERATING_SYSTEMS.MACOSX + ' ' + REGEX_VERSION_MAC)
  );
  if (macOsxVersionInUserAgentMatches) {
    const versionString = macOsxVersionInUserAgentMatches[1].replace(/_/g, '.');
    if (versionString) {
      const delimiter = getDelimiter(versionString);
      if (delimiter) {
        const components = versionString.split(delimiter);
        return components[0];
      }
      return versionString;
    }
  }
  return UNKNOWN;
};

export interface UserAgentInfo {
  browserName: string | undefined;
  browserVersion: string | undefined;
  osName: string | undefined;
  osVersion: string | undefined;
}

let cachedUserAgentInfo: UserAgentInfo | undefined = undefined;

export function getUserAgentInfo(): UserAgentInfo {
  if (!cachedUserAgentInfo) {
    const userAgent = getUserAgent();

    const browserName = getBrowserName(userAgent);
    const browserVersion = browserName !== undefined ? getBrowserVersion(userAgent, browserName) : undefined;
    const osName = getOsName(userAgent);
    const osVersion = osName !== undefined ? getOsVersion(userAgent, osName) : undefined;

    cachedUserAgentInfo = {
      browserName,
      browserVersion,
      osName,
      osVersion
    };
  }

  return cachedUserAgentInfo;
}
